/** Build a stack: pick the deputy and their hours; CivilHound ranks ready papers by likelihood of service. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate } from '../shared/format.ts';
import { Link } from '../shared/router.tsx';
import { Card, Empty, ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { hours } from '../shared/ScheduleCalendar.tsx';
import { PinLegend, SlippyMap, type MapMarker } from '../shared/SlippyMap.tsx';
import { pointInPolygon } from '@civilos/domain';

const todayIso = (tz: string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export function Stack() {
  const { me } = useAuth();
  const meta = useLoad(() => api.get('/api/meta'));
  const [f, setF] = useState({ serverId: '', date: todayIso(me!.agency.timezone), start: '16:00', end: '21:00', max: '20' });
  const [res, setRes] = useState<any>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [assigned, setAssigned] = useState<{ ids: string[]; msg: string } | null>(null);
  const [draw, setDraw] = useState<'off' | 'add' | 'remove'>('off');
  const [shapes, setShapes] = useState<{ lat: number; lng: number }[][]>([]);
  const [showNo, setShowNo] = useState(false);
  const a = useAction();
  const b = useAction();
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  // Who is scheduled that day (from Deputy schedule) → one click fills the deputy and hours.
  const day = useLoad(() => api.get<any>(`/api/schedule?from=${f.date}&to=${f.date}`).catch(() => null), [f.date]);
  const sched = (uid: string) => day.data?.schedule.find((e: any) => e.user_id === uid) ?? null;
  const sug = day.data?.suggestions?.[f.date];
  const pick = (uid: string) => {
    const e = sched(uid);
    const hrs = e?.kind === 'working' && e.start_time ? { start: e.start_time, end: e.end_time } : e?.kind === 'flexible' && sug && !sug.blocked ? { start: sug.start, end: sug.end } : {};
    setF({ ...f, serverId: uid, ...hrs });
  };
  const chosen = f.serverId ? sched(f.serverId) : null;

  const build = () =>
    a.run(async () => {
      setAssigned(null);
      const r = await api.post('/api/stack/suggest', { ...f, max: Number(f.max) || 20 });
      setRes(r);
      setSel(new Set(r.suggested));
      setShapes([]);
      setDraw('off');
    });
  const assign = () =>
    b.run(async () => {
      const ids = res.papers.filter((p: any) => sel.has(p.id)).map((p: any) => p.id);
      const r = await api.post('/api/papers/bulk', { action: 'assign', paperIds: ids, serverId: f.serverId, mode: 'retained' });
      const who = (meta.data?.servers ?? []).find((x: any) => x.id === f.serverId)?.display_name ?? 'the deputy';
      setAssigned({ ids, msg: `${r.okCount} paper${r.okCount === 1 ? '' : 's'} assigned to ${who}${r.failed ? `, ${r.failed} skipped` : ''}. They’re on the deputy’s “Papers to pick up.”` });
      setRes({ ...res, papers: res.papers.filter((p: any) => !ids.includes(p.id)) });
      setSel(new Set());
    });

  if (meta.loading) return <Spinner />;
  const toggle = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const all = res?.papers ?? [];
  const deputyName = (meta.data?.servers ?? []).find((x: any) => x.id === f.serverId)?.display_name ?? null;
  const onMap = all.filter((p: any) => p.lat != null && p.lng != null);
  const offMap = all.length - onMap.length;
  const markers: MapMarker[] = [
    ...onMap.map((p: any) => ({
      id: p.id,
      lat: p.lat,
      lng: p.lng,
      color: sel.has(p.id) ? '#e07b00' : '#2a78d6',
      label: String(all.indexOf(p) + 1),
      selected: sel.has(p.id),
      title: `${p.civilosNumber} ${p.lastName} — ${p.address} · ${p.chance == null ? 'no history' : `${p.chance}%`}${sel.has(p.id) ? ' · in the stack' : ''}`,
    })),
    ...(showNo ? res?.notEligible ?? [] : [])
      .filter((p: any) => p.lat != null)
      .map((p: any) => ({ id: `no:${p.id}`, lat: p.lat, lng: p.lng, color: '#8b95a5', dot: true, title: `${p.civilosNumber} ${p.lastName} — not eligible: ${p.reason}` })),
  ];
  const onDrawn = (outline: { lat: number; lng: number }[]) => {
    const inside = onMap.filter((p: any) => pointInPolygon({ lat: p.lat, lng: p.lng }, outline)).map((p: any) => p.id);
    setSel((s) => {
      const n = new Set(s);
      for (const id of inside) draw === 'remove' ? n.delete(id) : n.add(id);
      return n;
    });
    setShapes((x) => [...x, outline]);
  };
  const allOn = all.length > 0 && all.every((p: any) => sel.has(p.id));

  return (
    <div className="stack">
      <div>
        <h1>Build a stack</h1>
        <p className="muted">Choose the deputy and the hours they’ll be working. CivilHound ranks the papers ready to assign by how likely they are to be served in those hours.</p>
      </div>
      <Card>
        <form
          className="stack-form"
          onSubmit={(e) => {
            e.preventDefault();
            build();
          }}
        >
          <label className="field">
            <span className="field-l">Deputy</span>
            <select value={f.serverId} onChange={(e) => pick(e.target.value)} required aria-label="Deputy">
              <option value="">Choose…</option>
              {(meta.data?.servers ?? []).map((s: any) => (
                <option key={s.id} value={s.id}>
                  {s.display_name}
                  {s.badge_number ? ` (${s.badge_number})` : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-l">Date</span>
            <input type="date" value={f.date} onChange={set('date')} required />
          </label>
          <label className="field">
            <span className="field-l">Working from</span>
            <input type="time" value={f.start} onChange={set('start')} required aria-label="Working from" />
          </label>
          <label className="field">
            <span className="field-l">Until</span>
            <input type="time" value={f.end} onChange={set('end')} required aria-label="Until" />
          </label>
          <label className="field">
            <span className="field-l">Stack size</span>
            <input type="number" min="1" max="100" value={f.max} onChange={set('max')} style={{ width: 90 }} />
          </label>
          <button className="btn primary" disabled={a.busy}>
            {a.busy ? 'Building…' : f.serverId ? 'Build stack' : 'Show eligible papers'}
          </button>
        </form>
        {day.data && (
          <div className="stack-sched">
            <span className="muted small">Scheduled {new Date(`${f.date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'numeric', day: 'numeric', timeZone: 'UTC' })}:</span>
            {day.data.schedule.filter((e: any) => e.kind !== 'off').length === 0 && <span className="muted small">nobody yet</span>}
            {day.data.schedule
              .filter((e: any) => e.kind !== 'off')
              .map((e: any) => {
                const u = day.data.deputies.find((x: any) => x.id === e.user_id);
                return (
                  <button key={e.user_id} type="button" className={`btn sm ${f.serverId === e.user_id ? 'primary' : ''}`} onClick={() => pick(e.user_id)}>
                    {u?.display_name} · {e.kind === 'working' ? hours(e) : 'Flexible'}
                  </button>
                );
              })}
          </div>
        )}
        {chosen?.kind === 'off' && <Notice tone="warn">This deputy marked this day as off{chosen.note ? ` (${chosen.note})` : ''}.</Notice>}
        {chosen?.kind === 'flexible' && <Notice tone="info">This deputy is flexible this day{sug && !sug.blocked ? ` — hours filled from the suggestion (${sug.label})` : ''}.</Notice>}
        {chosen?.kind === 'working' && <p className="small muted">Hours filled from the deputy’s schedule ({hours(chosen)}{chosen.source === 'supervisor' ? ', set by supervisor' : ''}).</p>}
        <ErrorBox error={a.error} />
      </Card>

      {assigned && (
        <Notice tone="ok">
          {assigned.msg}{' '}
          <Link className="btn sm" to={`/papers/print?kind=labels&ids=${assigned.ids.join(',')}`}>
            Print labels
          </Link>{' '}
          <Link className="btn sm" to={`/papers/print?kind=covers&ids=${assigned.ids.join(',')}`}>
            Print cover sheets
          </Link>
        </Notice>
      )}

      {res && (
        <Card
          title={`${deputyName ?? 'Eligible papers'} · ${fmtDate(res.shift.date)} · ${res.shift.start}–${res.shift.end}`}
          actions={
            <button className="btn primary" disabled={!sel.size || b.busy || !f.serverId} onClick={assign} title={f.serverId ? undefined : 'Choose the deputy above first'}>
              {b.busy ? 'Assigning…' : f.serverId ? `Assign ${sel.size} to ${deputyName}` : `${sel.size} picked — choose a deputy to assign`}
            </button>
          }
        >
          <ErrorBox error={b.error} />
          {all.length > 0 && (
            <div className="stack-map">
              <div className="stack-map-bar" role="group" aria-label="Pick papers on the map">
                <strong>{all.length} eligible {res.shift.start}–{res.shift.end}</strong>
                <span className="muted small">· {sel.size} in the stack</span>
                <span className="grow" />
                <button type="button" className={`btn sm ${draw === 'add' ? 'primary' : ''}`} aria-pressed={draw === 'add'} onClick={() => setDraw(draw === 'add' ? 'off' : 'add')}>
                  ✏️ Draw to add
                </button>
                <button type="button" className={`btn sm ${draw === 'remove' ? 'primary' : ''}`} aria-pressed={draw === 'remove'} onClick={() => setDraw(draw === 'remove' ? 'off' : 'remove')}>
                  ⌫ Draw to remove
                </button>
                <button type="button" className="btn sm ghost" onClick={() => (setSel(new Set()), setShapes([]))}>
                  Clear
                </button>
                <label className="small row gap-sm">
                  <input type="checkbox" checked={showNo} onChange={(e) => setShowNo(e.target.checked)} /> Show not eligible
                </label>
              </div>
              {draw !== 'off' && (
                <p className="small stack-map-hint">
                  {draw === 'add' ? 'Drag around the papers to add them to the stack.' : 'Drag around the papers to take them out of the stack.'} Click a pin to add or remove one paper. Turn drawing off to move the map.
                </p>
              )}
              <SlippyMap
                height={460}
                markers={markers}
                fitKey={`${res.shift.date}|${res.shift.start}|${res.shift.end}|${all.length}`}
                drawing={draw !== 'off'}
                onDraw={onDrawn}
                shapes={shapes}
                onMarkerClick={(id) => !id.startsWith('no:') && toggle(id)}
                ariaLabel="Eligible papers map"
              />
              <div className="row between wrap gap">
                <PinLegend items={[{ color: '#e07b00', label: 'In the stack', count: onMap.filter((p: any) => sel.has(p.id)).length }, { color: '#2a78d6', label: 'Eligible, not picked' }, ...(showNo ? [{ color: '#8b95a5', label: 'Not eligible these hours' }] : [])]} />
                {offMap > 0 && <span className="small muted">{offMap} paper{offMap === 1 ? '' : 's'} have no map location — pick them in the list below.</span>}
              </div>
            </div>
          )}
          {all.length === 0 && <Empty>No ready papers can be attempted during these hours.</Empty>}
          {all.length > 0 && (
            <div className="table-wrap">
              <table className="table stack-table">
                <thead>
                  <tr>
                    <th className="check-col">
                      <input type="checkbox" aria-label="Select all" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(all.map((p: any) => p.id)))} />
                    </th>
                    <th>#</th>
                    <th>Paper</th>
                    <th>Chance this shift</th>
                    <th>Why</th>
                    <th>Court date</th>
                  </tr>
                </thead>
                <tbody>
                  {all.map((p: any, i: number) => (
                    <tr key={p.id} className={sel.has(p.id) ? 'selected' : ''} onClick={() => toggle(p.id)}>
                      <td className="check-col" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label={`Select ${p.civilosNumber}`} checked={sel.has(p.id)} onChange={() => toggle(p.id)} />
                      </td>
                      <td className="stack-rank">{i + 1}</td>
                      <td>
                        <div>
                          <span className="mono">{p.civilosNumber}</span> <strong>{p.lastName}</strong>
                        </div>
                        <div className="muted small">
                          {p.address} · {p.paperTypeCode.replace(/_/g, ' ').toLowerCase()}
                        </div>
                      </td>
                      <td className="stack-chance">{p.chance == null ? <span className="muted small">No history</span> : `${p.chance}%`}</td>
                      <td>
                        <div className="stack-why">
                          {p.reasons.map((r: string, k: number) => (
                            <span key={k}>{r}</span>
                          ))}
                        </div>
                      </td>
                      <td>{fmtDate(p.courtDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {res.notEligible.length > 0 && (
            <details className="stack-no">
              <summary>
                {res.notEligible.length} ready paper{res.notEligible.length === 1 ? '' : 's'} can’t be attempted during these hours
              </summary>
              <ul>
                {res.notEligible.map((p: any) => (
                  <li key={p.id}>
                    <span className="mono">{p.civilosNumber}</span> {p.lastName} — {p.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          <p className="small muted">Chance = share of past attempts on this person (all papers) that ended in service, for the time of day in this shift. Ranking also favors papers that need an attempt in this time window, urgent papers, papers close to their cutoff, and papers set for this day at reconciliation.</p>
        </Card>
      )}
    </div>
  );
}
